import type { JSONContent } from "@tiptap/react";
import type {
  CheckboxListField,
  ContentField,
  ContentNode,
  DateField,
  GridField,
  ScoreField,
  SelectField,
  StatementListField,
  TextField,
} from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";
import { calcScoreGroup } from "@/utils/scoring";

export function nodeTextContent(node: JSONContent): string {
  if (node.type === "text") {
    return node.text ?? "";
  }
  if (node.content) {
    return node.content.map((n) => nodeTextContent(n)).join("");
  }
  return "";
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatDateForReport(iso: string | undefined): string {
  if (!iso) {
    return "--";
  }
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) {
    return iso;
  }
  return dateFormatter.format(new Date(year, month - 1, day));
}

/** Strip common markdown markers for plain-text report output. */
export function stripMarkdown(text: string): string {
  return text
    .replaceAll(/\*\*([^*]+)\*\*/g, "$1") // **bold**
    .replaceAll(/\*([^*]+)\*/g, "$1") // *italic*
    .replaceAll(/_([^_]+)_/g, "$1"); // _italic_
}

function renderTextField(
  field: TextField,
  value: unknown,
  interpolate: (text: string) => string,
): string | null {
  const raw = typeof value === "string" ? value : null;
  const text = raw !== null && raw !== "" ? raw : interpolate(field.defaultValue);
  if (!text && field.optional) {
    return null;
  }
  const display = text ? interpolate(text) : "--";
  if (field.multiline && display !== "--") {
    const indented = display
      .split("\n")
      .map((line) => `  ${line}`)
      .join("\n");
    return field.label ? `${field.label}:\n${indented}` : indented;
  }
  return field.label ? `${field.label}: ${display}` : display;
}

function renderSelectField(field: SelectField, value: unknown): string {
  const selected = value as string;
  const display = selected === "" ? "--" : selected;
  return `${field.label}: ${display}`;
}

function renderCheckboxListField(field: CheckboxListField, value: unknown): string {
  const checked = (value as string[] | undefined) ?? [];
  const itemsToShow =
    checked.length > 0
      ? checked.map((item) => `  [x] ${item}`)
      : field.items.map((item) => `  [ ] ${item}`);
  return `${field.label}:\n${itemsToShow.join("\n")}`;
}

function renderStatementListField(field: StatementListField, value: unknown): string {
  const items =
    (value as { text: string; checked: boolean }[] | undefined) ??
    field.items.map((text) => ({ checked: true, text }));
  const checked = items.filter((item) => item.checked);
  if (checked.length === 0) {
    return `${field.label}:`;
  }
  const itemLines = checked.map((item) => `  - ${item.text}`);
  return `${field.label}:\n${itemLines.join("\n")}`;
}

function renderScoreField(field: ScoreField, value: unknown): string {
  const score = value as number | undefined;
  if (field.scoreOptions) {
    if (score !== undefined) {
      const opt = field.scoreOptions.find((o) => o.value === score);
      const optLine = opt ? `  [${opt.value}] ${opt.label}` : "";
      return optLine ? `${field.label}: ${score}\n${optLine}` : `${field.label}: ${score}`;
    }
    const optionLines = field.scoreOptions.map((o) => `  [${o.value}] ${o.label}`);
    return `${field.label}: --\n${optionLines.join("\n")}`;
  }
  return `${field.label}: ${score ?? "--"}`;
}

function getGridCellValue(
  gridData: Record<string, Record<string, string>>,
  rowLabel: string,
  col: string,
  rowDefault: string | undefined,
): string {
  const cellVal = gridData[rowLabel]?.[col];
  if (cellVal !== undefined && cellVal !== "") {
    return cellVal;
  }
  if (rowDefault !== undefined && rowDefault !== "") {
    return rowDefault;
  }
  return "";
}

function renderGridField(field: GridField, value: unknown): string {
  const gridData = (value as Record<string, Record<string, string>> | undefined) ?? {};
  const lines = [`${field.label}:`];
  const labelWidth = Math.max(0, ...field.rows.map((r) => r.label.length + 1));
  const colWidths = field.columns.map((col) => {
    const cellValues = field.rows.map((row) =>
      getGridCellValue(gridData, row.label, col, row.defaults?.[col]),
    );
    return Math.max(col.length, ...cellValues.map((v) => v.length));
  });
  const headerCells = field.columns.map((col, ci) => col.padEnd(colWidths[ci] ?? 0));
  lines.push(`  ${"".padEnd(labelWidth)} ${headerCells.join(" | ")}`);
  for (const row of field.rows) {
    const cells = field.columns.map((col, ci) =>
      getGridCellValue(gridData, row.label, col, row.defaults?.[col]).padEnd(colWidths[ci] ?? 0),
    );
    lines.push(`  ${(row.label + ":").padEnd(labelWidth)} ${cells.join(" | ")}`);
  }
  return lines.join("\n");
}

function renderDateField(field: DateField, value: unknown): string | null {
  const raw = typeof value === "string" ? value : null;
  const display = raw !== null && raw !== "" ? raw : field.defaultValue;
  if (!display && field.optional) {
    return null;
  }
  const formatted = formatDateForReport(display);
  return field.label ? `${field.label}: ${formatted}` : formatted;
}

/** Returns the computed string for a scoregroup variable, or null if the prop is not recognized. */
function resolveScoreGroupVariable(
  prop: string,
  groupId: string,
  subData: SubTemplateData,
): string | null {
  if (prop !== "sum" && prop !== "min" && prop !== "max" && prop !== "avg" && prop !== "count") {
    return null;
  }
  const result = calcScoreGroup(subData.content, groupId, subData.values);
  if (prop === "sum") {
    return String(result.sum);
  }
  if (prop === "min") {
    return String(result.min);
  }
  if (prop === "max") {
    return String(result.max);
  }
  if (prop === "avg") {
    return String(Math.round(result.avg * 10) / 10);
  }
  return String(result.count);
}

function buildSubInterpolate(
  subData: SubTemplateData,
  parentInterpolate: (text: string) => string,
): (text: string) => string {
  // Uses [^{}]* to prevent backtracking: keys cannot themselves contain braces.
  return (text: string): string => {
    return text.replaceAll(/\{([^{}]*)\}/g, (match, key: string) => {
      const colonIdx = key.indexOf(":");
      const groupId = colonIdx === -1 ? key : key.slice(0, colonIdx);
      const prop = colonIdx === -1 ? "" : key.slice(colonIdx + 1);
      return resolveScoreGroupVariable(prop, groupId, subData) ?? parentInterpolate(match);
    });
  };
}

interface SubNodeResult {
  line: string | null;
  sectionVisible: boolean;
  afterHidden: boolean;
}

function processSubTemplateNode(
  node: ContentNode | null,
  sectionVisible: boolean,
  afterHidden: boolean,
  subData: SubTemplateData,
  subInterpolate: (text: string) => string,
): SubNodeResult {
  if (!node) {
    return { afterHidden, line: null, sectionVisible };
  }
  if (node.type === "section") {
    return {
      afterHidden: true,
      line: null,
      sectionVisible: subData.sectionToggles[node.id] ?? true,
    };
  }
  if (node.type === "scoregroup") {
    return { afterHidden: true, line: null, sectionVisible };
  }
  if (!sectionVisible) {
    return { afterHidden, line: null, sectionVisible };
  }
  if (node.type === "heading") {
    return { afterHidden: false, line: node.text, sectionVisible };
  }
  if (node.type === "text") {
    const trimmed = node.markdown.trim();
    if (!trimmed && afterHidden) {
      return { afterHidden: false, line: null, sectionVisible };
    }
    return {
      afterHidden: false,
      line: subInterpolate(stripMarkdown(node.markdown)),
      sectionVisible,
    };
  }
  // node.type === "field"
  const rendered = renderField(
    node.field,
    subData.values[node.field.id],
    subData.subTemplateValues[node.field.id],
    subInterpolate,
  );
  return { afterHidden: false, line: rendered, sectionVisible };
}

function renderSubTemplateField(
  subData: SubTemplateData,
  interpolate: (text: string) => string,
): string {
  const subInterpolate = buildSubInterpolate(subData, interpolate);
  const lines: string[] = [];
  let sectionVisible = true;
  let afterHidden = false;
  for (const node of subData.content) {
    const result = processSubTemplateNode(
      node,
      sectionVisible,
      afterHidden,
      subData,
      subInterpolate,
    );
    sectionVisible = result.sectionVisible;
    afterHidden = result.afterHidden;
    if (result.line !== null) {
      lines.push(result.line);
    }
  }
  return lines.join("\n");
}

export function renderField(
  field: ContentField,
  value: unknown,
  subTemplateData: SubTemplateData | undefined,
  interpolate: (text: string) => string,
): string | null {
  switch (field.type) {
    case "text": {
      return renderTextField(field, value, interpolate);
    }
    case "select": {
      return renderSelectField(field, value);
    }
    case "checkbox-list": {
      return renderCheckboxListField(field, value);
    }
    case "statement-list": {
      return renderStatementListField(field, value);
    }
    case "score": {
      return renderScoreField(field, value);
    }
    case "grid": {
      return renderGridField(field, value);
    }
    case "date": {
      return renderDateField(field, value);
    }
    case "sub-template": {
      return subTemplateData ? renderSubTemplateField(subTemplateData, interpolate) : null;
    }
    default: {
      return null;
    }
  }
}
